"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import LocationPermissionModal from "@/components/location-permission-modal"
import { login } from "@/lib/auth"

export default function LoginPage() {
  const router = useRouter()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [pendingUser, setPendingUser] = useState<any>(null)

  useEffect(() => {
    // Reset database on first load
    if (typeof window !== "undefined") {
      const hasReset = sessionStorage.getItem("db-reset")
      if (!hasReset) {
        localStorage.clear()
        sessionStorage.setItem("db-reset", "true")
      }
    }
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setLoading(true)

    try {
      const user = await login(email, password)
      if (user) {
        setPendingUser(user)
        setShowLocationModal(true)
      } else {
        setError("Invalid email or password")
      }
    } catch (err) {
      setError("Login failed. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleLocationGranted = () => {
    setShowLocationModal(false)
    router.push("/group/general")
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cyan-50 to-blue-50 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center overflow-hidden">
            <picture>
              <source srcSet="/App Icon.svg" type="image/svg+xml" />
              <img src="/icon-512.png" alt="Bubble1 icon" className="w-full h-full" />
            </picture>
          </div>
          <CardTitle className="text-3xl font-bold text-cyan-600">Bubble1</CardTitle>
          <CardDescription className="text-base">Discover your local community</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12"
              />
            </div>
            {error && <p className="text-sm text-red-500 text-center">{error}</p>}
            <Button type="submit" className="w-full h-12 bg-cyan-500 hover:bg-cyan-600" disabled={loading}>
              {loading ? "Logging in..." : "Log In"}
            </Button>
          </form>

          <div className="text-center">
            <Button variant="link" className="text-cyan-600" onClick={() => router.push("/register")}>
              Don't have an account? Sign up
            </Button>
          </div>
        </CardContent>
      </Card>

      {showLocationModal && <LocationPermissionModal onSuccess={handleLocationGranted} />}
    </div>
  )
}
